import { Injectable } from "@angular/core";
import { Feature } from "app/bundles/requests/shared/models/feature";
import { Properties } from "app/bundles/requests/shared/models/properties";
import * as _ from "lodash-es";
import * as moment from "moment";
import { Client } from "../models/client";
import { IGeojsonRequest } from "../models/geojsonRequest";
import {
  ICitizenMedia,
  IForemanMedia,
  IRawServiceRequestDetail,
  IRawServiceRequestSummary,
  IServiceRequestDetail,
  IServiceRequestSummary,
  IWorkorderOrServiceRequestSummary,
  IWorkorderRequestSummary,
  RequestSourceTypeEnum,
} from "../models/request";
import { EventRequestById } from "../models/event";
import { IDocument, OriginEnum } from "../models/document";

@Injectable()
export class UtilService {
  convertFeatureToGeojson(features: Feature[]): IGeojsonRequest {
    features = features ? features : [];
    return { type: "FeatureCollection", features: features };
  }

  getRequestFromProperties(
    feature: Feature,
  ): IWorkorderOrServiceRequestSummary {
    const request: IWorkorderOrServiceRequestSummary = {
      id: feature.properties.id,
      type: feature.properties.type,
      requestNumber: feature.properties.requestNumber,
      isRelaunched: feature.properties.isRelaunched,
      isReactivated: feature.properties.isReactivated,
      description: feature.properties.description,
      service: feature.properties.service,
      status: feature.properties.status,
      estimatedCompletionDate: feature.properties.estimatedCompletionDate,
      formattedAddress: feature.properties.formattedAddress,
      geometry: feature.geometry,
      creationDatetime: feature.properties.creationDatetime,
      isSelected: feature.properties.isSelected,
      isFiltred: feature.properties.isFiltred,
      source: RequestSourceTypeEnum[feature.properties.source.toString()],
      responsible: feature.properties.responsible,
      hasExistingTask: feature.properties.hasExistingTask,
      borough: feature.properties.borough,
      serviceRequestId: feature.properties.serviceRequestId,
    };
    return request;
  }

  getRequestsFromGeojson(
    geojsonData: IGeojsonRequest,
  ): IWorkorderOrServiceRequestSummary[] {
    const requests: IWorkorderOrServiceRequestSummary[] = [];
    if (geojsonData && geojsonData.features) {
      for (const feature of geojsonData.features) {
        if (feature.properties) {
          const request: IWorkorderOrServiceRequestSummary =
            this.getRequestFromProperties(feature);
          request.geometry = feature.geometry;
          requests.push(request);
        }
      }
    }
    return requests;
  }

  getRequestCount(geojsonData: IGeojsonRequest): number {
    return geojsonData && geojsonData.features
      ? geojsonData.features.length
      : 0;
  }

  getPropertiesFromRequest(
    request: IWorkorderOrServiceRequestSummary,
  ): Feature {
    const feature: Feature = new Feature();
    feature.type = "Feature";
    feature.geometry = request.geometry;
    feature.properties.id = request.id;
    feature.properties.type =
      request.source === RequestSourceTypeEnum.workorderRequest
        ? (request as IWorkorderRequestSummary).type
        : null;
    feature.properties.hasExistingTask =
      request.source === RequestSourceTypeEnum.workorderRequest
        ? (request as IWorkorderRequestSummary).hasExistingTask
        : null;
    feature.properties.requestNumber = request.requestNumber;
    feature.properties.isRelaunched = request.isRelaunched;
    feature.properties.isReactivated = request.isReactivated;
    feature.properties.description = request.description;
    feature.properties.service = request.service;
    feature.properties.status = request.status;
    feature.properties.estimatedCompletionDate =
      request.estimatedCompletionDate;
    feature.properties.formattedAddress = request.formattedAddress;
    feature.properties.creationDatetime = request.creationDatetime;
    feature.properties.isSelected = request.isSelected === true ? true : false;
    feature.properties.source = request.source;
    feature.properties.borough = request.borough;
    feature.properties.responsible = request.responsible;
    return feature;
  }

  formatDelay(delay: number): number {
    return delay >= 1 ? Math.floor(delay) : delay;
  }

  public getDelay(date: Date): number {
    return moment(date).isValid()
      ? this.formatDelay(
          moment.duration(moment(new Date()).diff(moment(date))).asDays(),
        )
      : 0;
  }

  public hasDelayOver1Day(delayDays: number): boolean {
    return delayDays >= 1 ? true : false;
  }

  public hasDelayWithin1Day(delayDays: number): boolean {
    return delayDays > 0 && delayDays < 1 ? true : false;
  }

  public updateDefaultProperties(
    geoJsonRequest: IGeojsonRequest,
    source: RequestSourceTypeEnum,
  ): void {
    if (
      geoJsonRequest &&
      geoJsonRequest.features &&
      geoJsonRequest.features.length > 0
    ) {
      _.forEach(geoJsonRequest.features, (feature: Feature) => {
        feature.properties.isFiltred = true;
        feature.properties.isSelected = false;
        feature.properties.source = RequestSourceTypeEnum[source.toString()];
      });
    }
  }

  // this function will be remove as it seems useless
  resetSelection(geoJsonRequest: IGeojsonRequest) {
    if (
      geoJsonRequest &&
      geoJsonRequest.features &&
      geoJsonRequest.features.length > 0
    ) {
      _.forEach(geoJsonRequest.features, (feature: Feature) => {
        if (feature.properties.isSelected === true) {
          feature.properties.isSelected = true;
        } else {
          feature.properties.isSelected = false;
        }
      });
    }
  }

  clearSelectionFeature(geoJsonRequest: IGeojsonRequest) {
    if (
      geoJsonRequest &&
      geoJsonRequest.features &&
      geoJsonRequest.features.length > 0
    ) {
      _.forEach(geoJsonRequest.features, (feature: Feature) => {
        feature.properties.isSelected = false;
      });
    }
  }

  resetFilter(geoJsonRequest: IGeojsonRequest) {
    if (
      geoJsonRequest &&
      geoJsonRequest.features &&
      geoJsonRequest.features.length > 0
    ) {
      _.forEach(geoJsonRequest.features, (feature: Feature) => {
        feature.properties.isFiltred = false;
      });
    }
  }

  updateSelection(
    geoJsonRequest: IGeojsonRequest,
    request?: IWorkorderOrServiceRequestSummary,
  ) {
    if (
      geoJsonRequest &&
      geoJsonRequest.features &&
      geoJsonRequest.features.length > 0
    ) {
      _.forEach(geoJsonRequest.features, (feature: Feature) => {
        if (feature.properties.id === request.id) {
          feature.properties.isSelected = request.isSelected;
        } else {
          feature.properties.isSelected = false;
        }
      });
    }
  }

  updateSelectionFeature(
    geoJsonRequest: IGeojsonRequest,
    selectedProperties?: Properties,
  ) {
    if (
      geoJsonRequest &&
      geoJsonRequest.features &&
      geoJsonRequest.features.length > 0
    ) {
      _.forEach(geoJsonRequest.features, (feature: Feature) => {
        if (
          selectedProperties &&
          feature.properties.id === selectedProperties.id
        ) {
          feature.properties.isSelected = selectedProperties.isSelected;
        } else {
          feature.properties.isSelected = false;
        }
      });
    }
  }

  geojsonfilter(geoJsonRequest: IGeojsonRequest, parameters: any): void {
    if (
      geoJsonRequest &&
      geoJsonRequest.features &&
      geoJsonRequest.features.length > 0
    ) {
      _.forEach(geoJsonRequest.features, (feature: Feature) => {
        if (
          (parameters.serviceCode === "" ||
            feature.properties.service.code === parameters.serviceCode) &&
          (parameters.statusCode === "" ||
            feature.properties.status.code === parameters.statusCode) &&
          ((!feature.properties.responsible.userName &&
            parameters.responsibleUser === "NONE") ||
            parameters.responsibleUser === "" ||
            feature.properties.responsible.userName ===
              parameters.responsibleUser) &&
          (parameters.responsibleGroup === "" ||
            feature.properties.responsible.groupName ===
              parameters.responsibleGroup) &&
          (parameters.searchTerm === "" ||
            feature.properties.requestNumber.includes(parameters.searchTerm))
        ) {
          feature.properties.isFiltred = true;
        } else {
          feature.properties.isFiltred = false;
        }
      });
    }
  }

  getGeojsonCount(geoJsonRequest: IGeojsonRequest): number {
    return geoJsonRequest && geoJsonRequest.features
      ? geoJsonRequest.features.length
      : 0;
  }

  getCurrentGeojsonCount(geoJsonRequest: IGeojsonRequest): number {
    if (geoJsonRequest && geoJsonRequest.features) {
      const filtred: Feature[] = _.filter(
        geoJsonRequest.features,
        (o: Feature) => {
          return o.properties.isFiltred;
        },
      );
      return filtred.length;
    } else {
      return 0;
    }
  }

  getOrientation(): string {
    return window.matchMedia("(orientation: portrait)").matches
      ? "PORTRAIT"
      : "LANDSCAPE";
  }

  public formatRawServiceRequestToServiceRequest(
    serviceRequestToTransform: IRawServiceRequestDetail,
    requestSummary: IWorkorderOrServiceRequestSummary,
  ): IServiceRequestDetail {
    return {
      id: serviceRequestToTransform.serviceRequestId,
      requestNumber: serviceRequestToTransform.requestNumber,
      borough: serviceRequestToTransform.borough,
      clients: this.formatRawServiceRequestClientToClient(
        serviceRequestToTransform.clients,
      ),
      geometry: {
        type: "Point",
        coordinates: [
          serviceRequestToTransform.location.long,
          serviceRequestToTransform.location.lat,
        ],
      },
      status: {
        code: serviceRequestToTransform.status.code,
        name: serviceRequestToTransform.status.name,
        statusDatetime: serviceRequestToTransform.status.createdAt,
      },
      estimatedCompletionDate: this.getEstimatedCompletionDate(
        serviceRequestToTransform.requestedDatetime,
        serviceRequestToTransform.nbrDaysForResolution,
      ),
      creationDatetime: serviceRequestToTransform.requestedDatetime,
      formattedAddress: serviceRequestToTransform.formattedAddress,
      service: serviceRequestToTransform.activity,
      description: serviceRequestToTransform.subject,
      isReactivated: serviceRequestToTransform.isReactivated,
      isRelaunched: serviceRequestToTransform.isRelaunched,
      responsible: serviceRequestToTransform.responsible,
      source: RequestSourceTypeEnum.serviceRequest,
      foremanMedias: extractForemanMedias(serviceRequestToTransform.media),
      citizenMedias: extractCitizenMedias(serviceRequestToTransform.media),
      comments: serviceRequestToTransform.comments,
      events: this.formatRawServiceRequestInternalCommentsToEvents(
        serviceRequestToTransform,
      ),
      requestSummary,
    };
  }

  private formatRawServiceRequestInternalCommentsToEvents(
    serviceRequestToTransform: IRawServiceRequestDetail,
  ): EventRequestById[] {
    if (!serviceRequestToTransform?.internalComments?.length) {
      return [];
    }
    return serviceRequestToTransform.internalComments.map((e) => ({
      creationDate: new Date(e.creationDatetime),
      internalComment: e.internalComment,
      user: e.userCreatedBy?.userCode,
      userFirstName: e.userCreatedBy?.firstName,
      userLastName: e.userCreatedBy?.lastName,
    }));
  }

  public getEstimatedCompletionDate(
    date: Date,
    nbrDaysForResolution: number,
  ): Date {
    if (!(nbrDaysForResolution >= 0)) {
      return null;
    }
    const estimatedCompletionTime = new Date(_.cloneDeep(date));
    estimatedCompletionTime.setDate(
      estimatedCompletionTime.getDate() + nbrDaysForResolution,
    );
    const ret = estimatedCompletionTime;
    return ret;
  }

  public formatRawServiceRequestClientToClient(clients: any): Client[] {
    const retValue: Client[] = [];
    clients.forEach((client) => {
      retValue.push({
        name: client.name,
        telephones: client.phones,
        address: client.address,
        comment: client.comment,
        email: client.email,
        creationDatetime: client.creationDatetime,
      });
    });
    return retValue;
  }
}

export function extractForemanMedias(medias: IDocument[]): IForemanMedia[] {
  return medias
    .filter((m) => m.origin === OriginEnum.foreman)
    .map((m) => ({
      url: m.url,
      description: m.description,
      origin: OriginEnum.foreman,
    }));
}

export function extractCitizenMedias(medias: IDocument[]): ICitizenMedia[] {
  return medias
    .filter((m) => m.origin === OriginEnum.citizen)
    .map((m) => ({
      url: m.url,
      description: m.description,
      origin: OriginEnum.citizen,
    }));
}
